import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../utils/constants';
import { showErrorToast, showSuccessToast } from '../utils/toast';
import * as calendarService from '../services/calendarService';

// Get calendar events query
export const useCalendarEvents = (userId, userRole, options = {}) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.CALENDAR.EVENTS, userId, userRole],
    queryFn: () => calendarService.getCalendarEvents(userId, userRole),
    enabled: !!userId && !!userRole && (options.enabled !== false),
    staleTime: 1 * 60 * 1000, // 1 minute
    ...options,
  });
};

// Get availability query
export const useAvailability = (employeeId, options = {}) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.CALENDAR.AVAILABILITY, employeeId],
    queryFn: () => calendarService.getAvailability(employeeId),
    enabled: !!employeeId && (options.enabled !== false),
    ...options,
  });
};

// Mark availability mutation
export const useMarkAvailability = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, date }) => calendarService.markAvailability(employeeId, date),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CALENDAR.EVENTS });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.CALENDAR.AVAILABILITY, variables.employeeId] });
      showSuccessToast('Availability marked successfully!');
    },
    onError: (error) => {
      showErrorToast(error, 'Failed to mark availability. Please try again.');
    },
  });
};

// Remove availability mutation
export const useRemoveAvailability = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, date }) => calendarService.removeAvailability(employeeId, date),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CALENDAR.EVENTS });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.CALENDAR.AVAILABILITY, variables.employeeId] });
      showSuccessToast('Availability removed.');
    },
    onError: (error) => {
      showErrorToast(error, 'Failed to remove availability. Please try again.');
    },
  });
};

// Get events by date range
export const useEventsByDateRange = (userId, userRole, startDate, endDate, options = {}) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.CALENDAR.EVENTS, userId, userRole, startDate, endDate],
    queryFn: () => calendarService.getEventsByDateRange(userId, userRole, startDate, endDate),
    enabled: !!userId && !!userRole && !!startDate && !!endDate && (options.enabled !== false),
    ...options,
  });
};











